<template>
    <div
        class="gradient-shape"
        :class="shape"
        :style="{
            background: gradient
        }"
    ></div>
</template>

<script setup lang="ts">
    import { type ThemeName, usePlatformMeta } from '~/composables/usePlatformContext';
    import type { GradientTheme as TeaserStoryGradientTheme } from '~/storyblok/TeaserStory/TeaserStoryStep.vue';

    /**
     * Themes passed directly in code — not from Storyblok schemas.
     * SB components forward their own theme props; literals at call sites:
     * - white: LayoutFooter
     * - gray: TopStory
     * - white-pink: NewsCard (fallback)
     * - pta-primary, pta-secondary: FamilyTopStory
     */
    type InternalTheme = 'white' | 'white-pink' | 'gray' | 'pta-primary' | 'pta-secondary';
    type BlogTeaserTheme = Exclude<BlogTeaserStoryblok['theme'], '' | undefined>;
    type BlogTeaserElementTheme = TeaserElementStoryblok['layout'];
    type SharedTheme = BlogTeaserElementTheme | BlogTeaserTheme | TeaserStoryGradientTheme | InternalTheme;
    type GradientsMap = Partial<Record<ThemeName | 'default', Partial<Record<SharedTheme, [string, string]>>>>;

    const {
        shape = 'circle',
        theme = 'standard',
        gradientStart = '0%',
        gradientEnd = '100%',
        gradientAngle = '0deg'
    } = defineProps<{
        shape?: 'leaf' | 'circle';
        theme?: SharedTheme;
        gradientStart?: string;
        gradientEnd?: string;
        gradientAngle?: string;
    }>();

    // global theme name for the current section
    const { theme: themeName } = usePlatformMeta();

    const fallbackColors = ['rgba(237, 171, 0,0)', 'rgba(237, 171, 0,1)'];
    const themeGradients: GradientsMap = {
        default: {
            academy: ['rgba(164, 198, 205, 0)', 'rgba(164, 198, 205, 1)'],
            beige: ['#e4d5c800', '#e4d5c8ff'],
            'beige-light': ['#fef8f200', '#fef8f2ff'],
            blue: ['#74b9c300', '#74b9c3ff'],
            'blue-light': ['#a7dce400', '#a7dce4ff'],
            family: ['rgba(67, 139, 152, 0)', 'rgba(67, 139, 152, 1)'],
            gray: ['rgba(160, 160, 160, 0)', 'rgba(160, 160, 160, 1)'],
            green: ['#95d5bb00', '#95d5bbff'],
            'green-light': ['#c9f4e300', '#c9f4e3ff'],
            orange: ['#e6c17100', '#e6c171ff'],
            'orange-light': ['#ffd67e00', '#ffd67eff'],
            pink: ['#c9a5bb00', '#c9a5bbff'],
            'white-pink': ['#c9a5bb00', '#c9a5bbff'], // same as pink
            'pta-primary': ['#b70f80ff', '#b70f8000'],
            'pta-secondary': ['#f39324ff', '#f3932400'],
            'red-light': ['#f1b5b500', '#f1b5b5ff'],
            purple: ['#b69ec200', '#b69ec2ff'],
            'purple-light': ['#dac8e400', '#dac8e4ff'],
            red: ['#d4868600', '#d48686ff'],
            shop: ['rgba(224, 210, 195, 0)', 'rgba(224, 210, 195, 1)'],
            standard: ['rgba(206, 224, 226, 0)', 'rgba(206, 224, 226, 1)'],
            white: ['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 1)']
        },
        'pta-family': {
            blue: ['#027f9500', '#027f9550'],
            orange: ['#f39324', '#f28100'],
            pink: ['#b70f8000', '#b70f8050'],
            'white-pink': ['#b70f8000', '#b70f8050'] // same as pink
        },
        urology: {
            blue: ['#D8B8D100', '#004C7D1A'],
            orange: ['#ffb24d33', '#d8b8d100'],
            pink: ['#D8B8D100', '#B70F801A']
        },
        nephrology: {
            red: ['#CD071E00', '#CD071E1A'],
            green: ['#0C9D7100', '#0C9D711A'],
            blue: ['#005FAB00', '#005FAB1A'],
            orange: ['#EF791100', '#EF79111A']
        }
    };

    const gradient = computed(() => {
        const [startColor, endColor] =
            themeGradients[themeName.value]?.[theme] ?? themeGradients['default']?.[theme] ?? fallbackColors;

        return `linear-gradient(${gradientAngle},${startColor} ${gradientStart},${endColor} ${gradientEnd})`;
    });
</script>

<style lang="scss" scoped>
    @import '~/assets/scss/basics/gradient-shape.scss';
</style>
